import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import textCardImg from '../../../assets/images/servicepage/textcard.webp';
import TitleHeader from '../../atoms/TitleHeader/TitleHeader'
import Constants from '../../../app/utils/constants';
import colors from '../../theme/color';

const developmentGlowColorArray = [
  colors.serviceDevelopmentImageGlowA,
  colors.serviceDevelopmentImageGlowA,
  colors.serviceDevelopmentImageGlowC,
];

const imageCardClassName =
  'group relative flex aspect-[43/50] w-full items-center justify-center overflow-hidden rounded-none border bg-service-development-image-card px-6 py-5 shadow-none sm:px-7 sm:py-6 xl:px-7 xl:py-6 2xl:px-10 2xl:py-9 min-[2560px]:px-12 min-[2560px]:py-11';

  const textCardClassName =
  'group relative w-full overflow-hidden rounded-none border bg-service-development-text-card px-5 py-5 shadow-none aspect-auto min-h-[220px] sm:aspect-square sm:px-6 sm:py-7 xl:px-8 xl:py-10 2xl:px-10 2xl:py-12 min-[2560px]:px-12 min-[2560px]:py-14';

const imageImgClassNames = ['w-[64%] sm:w-[66%]', 'w-[64%] sm:w-[66%]', 'w-[68%] sm:w-[70%]'];

const DevelopmentSection = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const glow1Ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  React.useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const glowWidth = 500;
    const glowHeight = 700;
    const BLEED = 0.35;

    type SineLayer = { freq: number; amp: number; phase: number };

    const g1 = {
      x: [
        { freq: 0.00017, amp: 0.38, phase: 0.00 },
        { freq: 0.00041, amp: 0.22, phase: 1.57 },
        { freq: 0.00071, amp: 0.12, phase: 3.10 },
        { freq: 0.00113, amp: 0.06, phase: 0.88 },
      ],
      y: [
        { freq: 0.00023, amp: 0.38, phase: 2.30 },
        { freq: 0.00059, amp: 0.22, phase: 0.44 },
        { freq: 0.00083, amp: 0.12, phase: 1.20 },
        { freq: 0.00137, amp: 0.06, phase: 4.10 },
      ],
    };

    const noise = (layers: SineLayer[], t: number): number => {
      const totalAmp = layers.reduce((s, l) => s + l.amp, 0);
      const raw = layers.reduce((s, l) => s + Math.sin(t * l.freq + l.phase) * l.amp, 0);
      return raw / totalAmp; // -1 .. 1
    };

    let t = 0;

    const animate = () => {
      t += 16;
      const rect = section.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;
      const bleedX = glowWidth * BLEED;
      const bleedY = glowHeight * BLEED;
      const rangeX = W - glowWidth + bleedX * 2;
      const rangeY = H - glowHeight + bleedY * 2;

      const x1 = (noise(g1.x, t) * 0.5 + 0.5) * rangeX - bleedX;
      const y1 = (noise(g1.y, t) * 0.5 + 0.5) * rangeY - bleedY;

      if (glow1Ref.current) {
        glow1Ref.current.style.transform = `translate(${x1}px, ${y1}px)`;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);
  const development = Constants.servicePage.development;
  const titleBadgeBorderClassName = 'bg-service-title-badge';
  const developmentCards = [
    { type: 'image', ...development.imageItems[0] },
    { type: 'text', ...development.textItems[0] },
    { type: 'image', ...development.imageItems[1] },
    { type: 'text', ...development.textItems[1] },
    { type: 'image', ...development.imageItems[2] },
    { type: 'text', ...development.textItems[2] },
  ] as const;
  const desktopColumns = development.desktopColumnIndexes.map((column) =>
    column.map((cardIndex) => ({ item: developmentCards[cardIndex], idx: cardIndex }))
  );

  // Two-phase reveal: first all image cards, then all text cards.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start 72%', 'end 42%'],
  });

  const imageScale = useTransform(scrollYProgress, [0.02, 0.18], [0.86, 1]);
  const imageOpacity = useTransform(scrollYProgress, [0.02, 0.16], [0, 1]);

  const textScale = useTransform(scrollYProgress, [0.2, 0.34], [0.88, 1]);
  const textOpacity = useTransform(scrollYProgress, [0.2, 0.32], [0, 1]);

  const getCardMotionByIndex = (idx: number, type: 'image' | 'text') => {
    if (type === 'image') {
      return {
        scale: imageScale,
        opacity: imageOpacity,
        transformOrigin: '50% 50%',
      };
    }

    return {
      scale: textScale,
      opacity: textOpacity,
      transformOrigin: '50% 50%',
    };
  };

  return (
    <section
      ref={sectionRef}
      className="relative z-10 w-full overflow-clip px-6 py-14 sm:px-10 sm:py-16 lg:px-16 lg:py-16 xl:min-h-[100vh] xl:px-24 xl:py-20 2xl:min-h-[140vh] 2xl:px-32 2xl:py-24 min-[2560px]:min-h-[150vh] min-[2560px]:px-44 min-[2560px]:py-32"
    >
  <div
  ref={glow1Ref}
  className="pointer-events-none absolute top-0 left-0 -z-10 h-[700px] w-[500px] rounded-full blur-[120px] opacity-60"
  style={{ backgroundColor: colors.pinkGlowHex }}
/>
      
      <div className="mx-auto flex max-w-[1920px] flex-col items-center 2xl:sticky 2xl:top-6 min-[2560px]:top-8 2xl:max-w-[2200px] min-[2560px]:max-w-[2500px]">
        <div className="mb-12 text-center lg:mb-14">
          <TitleHeader
            badgeText={development.badgeText}
            titleLines={development.titleLines}
            wrapperClassName="flex flex-col items-center"
            titleClassName="text-4xl md:text-5xl lg:text-[44px] xl:text-[52px] 2xl:text-[62px] min-[2560px]:text-[82px] font-rajdhani font-semibold text-white leading-tight"
            badgeClassName="px-10 py-3 text-2xl md:px-12 md:py-4 md:text-3xl min-[1440px]:px-14 min-[1440px]:text-[2rem]"
            badgeBorderClassName={titleBadgeBorderClassName}
          />
        </div>

        <div className="grid w-full max-w-[720px] lg:max-w-[930px] grid-cols-1 gap-6 md:grid-cols-3 md:gap-7 xl:hidden">
  {developmentCards.map((item, idx) =>
    item.type === 'image' ? (
      <motion.div
        key={item.alt}
        className={`${imageCardClassName} hidden md:flex`}
        style={{ borderColor: colors.serviceDevelopmentImageBorder }}
      >
        <div className="absolute inset-0 bg-service-development-card-radial" />

        <div
          className="absolute inset-0 opacity-60 blur-[86px]"
          style={{ backgroundColor: developmentGlowColorArray[Math.floor(idx / 2)] }}
        />

        <img
          src={item.image}
          alt={item.alt}
          className={`relative z-10 object-contain transition-transform duration-300 group-hover:scale-[1.015] ${imageImgClassNames[Math.floor(idx / 2)]}`}
          style={{ filter: `drop-shadow(0 18px 24px ${colors.serviceDevelopmentShadow})` }}
        />
      </motion.div>
    ) : (
      <motion.article
        key={item.title.join(' ')}
        className={`${textCardClassName} relative`}
        style={{
          borderColor: colors.serviceDevelopmentTextBorder,
          backgroundImage: `url(${textCardImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-service-development-text-overlay" />

        {/* MOBILE ICON */}
        <div className="absolute top-4 right-4 z-20 flex h-14 w-14 items-center justify-center rounded-full backdrop-blur-md md:hidden">
          <img
            src={development.imageItems[Math.floor(idx / 2)].image}
            alt=""
            className="h-[95%] w-[95%] object-contain"
          />
        </div>

        <div className="relative z-10 flex h-auto flex-col justify-start">
          <h3 className="mb-3 font-rajdhani text-[25px] font-semibold leading-[1.08] text-white md:text-[15px] lg:text-[25px] xl:text-[30px]">
            {item.title[0]}
            <br />
            {item.title[1]}
          </h3>

          <p
            className="max-w-[240px] font-sans text-[15px] leading-[1.2] xl:leading-[1.45] sm:max-w-[252px] md:text-[10px] lg:text-[13px] xl:text-[16px]"
            style={{ color: colors.serviceDevelopmentBody }}
          >
            {item.body}
          </p>
        </div>
      </motion.article>
    )
  )}
</div>

        <div className="hidden w-full max-w-[1400px] 2xl:max-w-[1800px] min-[2560px]:max-w-[2300px] xl:grid xl:grid-cols-3 xl:gap-10 2xl:gap-14 min-[2560px]:gap-16">
          {desktopColumns.map((column, columnIndex) => (
            <div key={`column-${columnIndex}`} className="flex flex-col gap-10 2xl:gap-14 min-[2560px]:gap-16">
              {column.map(({ item, idx }) =>
                item.type === 'image' ? (
                  <motion.div
                    key={item.alt}
                    className={imageCardClassName}
                    style={{
                      borderColor: colors.serviceDevelopmentImageBorder,
                      ...getCardMotionByIndex(idx, 'image'),
                    }}
                  >
                    <div className="absolute inset-0 bg-service-development-card-radial" />
                    <div
                      className="absolute inset-0 opacity-60 blur-[86px]"
                      style={{ backgroundColor: developmentGlowColorArray[Math.floor(idx / 2)] }}
                    />
                    <img
                      src={item.image}
                      alt={item.alt}
                      className={`relative z-10 object-contain transition-transform duration-300 group-hover:scale-[1.015] ${imageImgClassNames[Math.floor(idx / 2)]}`}
                      style={{ filter: `drop-shadow(0 18px 24px ${colors.serviceDevelopmentShadow})` }}
                    />
                  </motion.div>
                ) : (
                  <motion.article


                  
                    key={item.title.join(' ')}
                    className={textCardClassName}
                    style={{
                      borderColor: colors.serviceDevelopmentTextBorder,
                      backgroundImage: `url(${textCardImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      ...getCardMotionByIndex(idx, 'text'),
                    }}
                  >
                    <div className="absolute inset-0 bg-service-development-text-overlay" />
                    <div className="relative z-10 flex h-full flex-col justify-start">
                      <h3 className="mb-4 font-rajdhani text-[30px] font-semibold leading-[1.08] text-white 2xl:text-[38px] min-[2560px]:text-[52px]">
                        {item.title[0]}
                        <br />
                        {item.title[1]}
                      </h3>
                      <p
                        className="max-w-[290px] font-sans text-[15px] leading-[1.45] 2xl:max-w-[360px] 2xl:text-[20px] min-[2560px]:max-w-[500px] min-[2560px]:text-[28px]"
                        style={{ color: colors.serviceDevelopmentBody }}
                      >
                        {item.body}
                      </p>
                    </div>
                  </motion.article>
                )
              )}
            </div>
          ))}
        </div>


      </div>
    </section>
  );
};

export default DevelopmentSection;
